import React from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Inbox } from "lucide-react";
import { BRIEF_TIME, ITEMS } from "./brief/briefData";
import { BriefView } from "./brief/BriefView";
import { DraftView } from "./brief/DraftView";

export default function MorningBriefDemo() {
  const [openInbox, setOpenInbox] = React.useState(false);
  const [activeId, setActiveId] = React.useState(null);

  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20% 0px" });

  React.useEffect(() => {
    if (inView) {
      const t = setTimeout(() => setOpenInbox(true), 600);
      return () => clearTimeout(t);
    }
  }, [inView]);

  const active = ITEMS.find((i) => i.id === activeId);

  return (
    <section
      id="brief"
      ref={ref}
      data-testid="morning-brief-section"
      className="relative bg-[#F3EFE6] py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute -right-32 -top-32 w-[420px] h-[420px] dot-grid opacity-50 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <p className="kicker">The wow moment</p>
            <h2 className="h-section text-[clamp(2rem,4vw,3.4rem)] mt-5">
              Watch a 6am Morning Brief land.
            </h2>
            <p className="font-serif-body text-[#4A4A4A] text-[1.1rem] leading-relaxed mt-6 max-w-md">
              This lands in your inbox at 6am local. You triage in four
              minutes. You're done before your first coffee. Click any item
              to read the draft.
            </p>
            <div className="mt-8 flex items-center gap-3 text-[13px] font-sans-tight text-[#4A4A4A]">
              <span className="inline-block w-2 h-2 rounded-full bg-[#E07B3A]" />
              Live simulation — these are real drafts a roofer would send.
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="inbox-shell rounded-sm" data-testid="inbox-shell">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-[#E5E0D8]">
                <span className="w-3 h-3 rounded-full bg-[#E5E0D8]" />
                <span className="w-3 h-3 rounded-full bg-[#E5E0D8]" />
                <span className="w-3 h-3 rounded-full bg-[#E5E0D8]" />
                <span className="font-mono-spec text-[11px] text-[#4A4A4A] ml-3">
                  inbox · tom@oakridge-roofing.com
                </span>
                <span className="ml-auto font-mono-spec text-[11px] text-[#4A4A4A]">
                  {BRIEF_TIME}
                </span>
              </div>

              <div className="grid sm:grid-cols-[260px_1fr] min-h-[520px]">
                <div className="border-r border-[#E5E0D8] bg-[#FBF9F4]">
                  <AnimatePresence>
                    {openInbox && (
                      <motion.button
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        onClick={() => setActiveId("__brief__")}
                        data-testid="brief-email-item"
                        className={`w-full text-left px-4 py-4 border-b border-[#E5E0D8] hover:bg-white transition-colors ${
                          activeId === "__brief__" || activeId === null ? "bg-white" : ""
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Inbox size={14} className="text-[#E07B3A]" />
                          <span className="font-sans-tight font-semibold text-[13px] text-[#0B3D5C]">
                            LeadRiver
                          </span>
                          <span className="ml-auto font-mono-spec text-[11px] text-[#4A4A4A]">
                            {BRIEF_TIME}
                          </span>
                        </div>
                        <p className="font-sans-tight font-semibold text-[14px] text-[#1A1A1A] mb-1 leading-snug">
                          Your morning brief — 7 things, 4 minutes
                        </p>
                        <p className="font-serif-body text-[12.5px] text-[#4A4A4A] line-clamp-2 leading-snug">
                          3 new leads, 2 aging estimates, 1 invoice past 30,
                          1 referral opening. Drafts attached.
                        </p>
                      </motion.button>
                    )}
                  </AnimatePresence>

                  <div className="px-4 py-4 opacity-50">
                    <p className="font-sans-tight font-semibold text-[13px] text-[#1A1A1A]">
                      Supplier · Beacon Building
                    </p>
                    <p className="font-serif-body text-[12px] text-[#4A4A4A] mt-1">
                      Order #88231 confirmed
                    </p>
                  </div>
                  <div className="px-4 py-4 opacity-40">
                    <p className="font-sans-tight font-semibold text-[13px] text-[#1A1A1A]">
                      Insurance · State Farm
                    </p>
                    <p className="font-serif-body text-[12px] text-[#4A4A4A] mt-1">
                      Claim 4729 — adjustor scheduled
                    </p>
                  </div>
                </div>

                <div className="p-7">
                  {(activeId === null || activeId === "__brief__") && (
                    <BriefView onPick={setActiveId} />
                  )}
                  {active && (
                    <DraftView
                      item={active}
                      onBack={() => setActiveId("__brief__")}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
